import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import CTAButton from "../components/CTAButton";
import Eyebrow from "../components/Eyebrow";
import { KineticLines, Reveal, FadeIn } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";
import { trackEvent } from "../lib/analytics";
import { heroConversation, dorisPortrait } from "../lib/assets";
import { GalleryPending } from "../components/FacilityGallery";
import {
  SITE,
  NAVIGATOR_PATHWAYS,
  HOME_CARE_OPTIONS,
  HOME_STEPS,
  FACILITIES,
  TESTIMONIALS,
  CATEGORY_LINE,
} from "../lib/site";

const PREVIEW_HOMES = ["monogram-villa", "allure-senior-living", "estar"];

function Hero() {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 56]);
  const yCard = useTransform(scrollY, [0, 700], [0, -28]);

  return (
    <section className="relative overflow-hidden bg-ivory pt-[72px]">
      <div
        className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-sage-mist"
        aria-hidden="true"
      />
      <div className="mx-auto grid max-w-site items-center gap-14 px-4 pb-20 pt-12 sm:px-6 md:pt-16 lg:grid-cols-12 lg:gap-10 lg:px-12 lg:pb-28 lg:pt-20">
        <div className="lg:col-span-7">
          <FadeIn>
            <Eyebrow>Senior Care &amp; Placement Guidance • Los Angeles</Eyebrow>
          </FadeIn>
          <h1 className="mt-7 font-serif text-[2.5rem] font-medium leading-[1.14] tracking-tight text-forest sm:text-5xl lg:text-[3.9rem]">
            <KineticLines
              delay={0.15}
              lines={[
                <>Finding the right care</>,
                <>starts with</>,
                <>
                  <em className="italic">understanding the person.</em>
                </>,
              ]}
            />
          </h1>
          <FadeIn delay={0.7}>
            <p className="mt-7 max-w-xl text-lg leading-[1.7] text-charcoal/80 md:text-xl">
              CareCraft helps families understand their options and find senior care that fits the
              person, their needs, and what matters to them.
            </p>
            <p className="mt-5 flex items-center gap-2.5 font-medium text-forest">
              <span className="h-2 w-2 rounded-full bg-clay" aria-hidden="true" />
              Complimentary guidance for families.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <CTAButton to="/contact" event="talk_with_doris" testId="hero-cta-talk-with-doris">
                Talk With Doris
              </CTAButton>
              <CTAButton
                to="/care-options"
                variant="outline"
                event="explore_care_options"
                testId="hero-cta-explore-care-options"
              >
                Explore Care Options
              </CTAButton>
            </div>
            <p className="mt-8 text-[0.8rem] font-medium uppercase tracking-[0.14em] text-charcoal/55">
              {CATEGORY_LINE}
            </p>
          </FadeIn>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={reduce ? undefined : { y }} className="relative">
            <figure className="relative">
              <div className="overflow-hidden rounded-2xl border border-inkline">
                <img
                  src={heroConversation}
                  alt="Two older adults having a conversation in a residential setting."
                  width={1600}
                  height={1076}
                  fetchPriority="high"
                  className="aspect-[5/4] w-full object-cover"
                />
              </div>
            </figure>
          </motion.div>
          <motion.div
            style={reduce ? undefined : { y: yCard }}
            className="absolute -bottom-8 -left-6 hidden max-w-[240px] rounded-2xl border border-ivory/10 bg-forest p-6 shadow-[0_20px_50px_rgba(20,46,40,0.25)] sm:block lg:-left-14"
          >
            <p className="font-serif text-lg italic leading-snug text-ivory">
              "Understand the person. Then find the care."
            </p>
            <p className="mt-3 text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-sage">
              The CareCraft principle
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Navigator() {
  return (
    <section className="bg-warmwhite py-20 md:py-28">
      <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
        <Reveal>
          <Eyebrow>Where would you like to begin?</Eyebrow>
          <h2 className="mt-6 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight text-forest md:text-4xl lg:text-[2.9rem]">
            Let's begin with what matters most.
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-[1.7] text-charcoal/75">
            Every person's needs, routines and preferences are different. Share what matters to you
            and your family, and CareCraft will help you explore care options that feel right for
            this next chapter.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-7">
          {NAVIGATOR_PATHWAYS.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.06}>
              <Link
                to="/find-care"
                data-testid={`pathway-card-${p.id}`}
                onClick={() => trackEvent("pathway_click", { pathway: p.id })}
                className="group flex h-full flex-col border-t-2 border-clay/50 bg-transparent pb-2 pt-5 transition-colors duration-300 hover:border-clay"
              >
                <span className="font-sans text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-forest">
                  {p.label}
                </span>
                <span className="mt-3 flex-1 font-serif text-[1.15rem] leading-[1.5] text-charcoal/80">
                  {p.body}
                </span>
                <span className="mt-5 inline-flex items-center gap-2 text-[0.88rem] font-medium text-forest transition-colors group-hover:text-clay">
                  Explore
                  <ArrowRight
                    className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CareOptions() {
  return (
    <section className="bg-ivory py-20 md:py-28">
      <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
        <Reveal>
          <Eyebrow>Care options</Eyebrow>
          <h2 className="mt-6 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight text-forest md:text-4xl lg:text-[2.9rem]">
            Care that fits the way life is lived.
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-[1.7] text-charcoal/75">
            Every person is different. Understanding the care options available can help families
            find the setting and support that feel right.
          </p>
        </Reveal>
        <div className="mt-12 divide-y divide-inkline border-y border-inkline">
          {HOME_CARE_OPTIONS.map((o, i) => (
            <Reveal key={o.id} delay={0.04 * i}>
              <Link
                to={`/care-options#${o.id}`}
                data-testid={`care-option-${o.id}`}
                onClick={() => trackEvent("care_option_click", { option: o.id })}
                className="group grid items-baseline gap-2 py-8 transition-colors md:grid-cols-12 md:gap-8"
              >
                <p className="font-serif text-lg text-clay md:col-span-1" aria-hidden="true">
                  {o.num}
                </p>
                <h3 className="font-serif text-2xl font-medium text-forest transition-colors group-hover:text-clay md:col-span-4 md:text-[1.7rem]">
                  {o.title}
                </h3>
                <p className="leading-[1.65] text-charcoal/80 md:col-span-6">{o.body}</p>
                <span className="hidden justify-self-end md:col-span-1 md:inline-flex" aria-hidden="true">
                  <ArrowRight className="h-5 w-5 text-forest/50 transition-all duration-300 group-hover:translate-x-1 group-hover:text-clay" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomesPreview() {
  const homes = FACILITIES.filter((f) => PREVIEW_HOMES.includes(f.id));
  return (
    <section className="bg-warmwhite py-20 md:py-28">
      <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
        <Reveal>
          <Eyebrow>Board &amp; Care homes</Eyebrow>
          <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
            <h2 className="max-w-xl font-serif text-3xl font-medium leading-tight tracking-tight text-forest md:text-4xl lg:text-[2.9rem]">
              Board &amp; Care homes to explore
            </h2>
            <Link
              to="/board-and-care"
              data-testid="homes-preview-link-all"
              onClick={() => trackEvent("explore_board_and_care_homes")}
              className="inline-flex items-center gap-2 pb-1 text-[0.95rem] font-medium text-forest transition-colors hover:text-clay"
            >
              Explore All Board &amp; Care Homes
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          <p className="mt-5 max-w-2xl text-lg leading-[1.7] text-charcoal/75">
            Explore some of the personally vetted residential care homes CareCraft can help your
            family consider.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-7 md:grid-cols-3">
          {homes.map((f, i) => (
            <Reveal key={f.id} delay={0.06 * i}>
              <article
                data-testid={`home-preview-card-${f.id}`}
                className="group flex h-full flex-col"
              >
                <Link
                  to={`/board-and-care#${f.id}`}
                  className="block overflow-hidden rounded-2xl border border-inkline"
                  aria-label={`Explore ${f.name}`}
                >
                  {f.photos.length > 0 ? (
                    <img
                      src={f.photos[0].src}
                      alt={f.photos[0].alt}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                    />
                  ) : (
                    <GalleryPending facilityId={f.id} />
                  )}
                </Link>
                <h3 className="mt-5 font-serif text-xl font-medium text-forest">{f.name}</h3>
                <p className="mt-1 text-[0.85rem] text-charcoal/60">{f.location}, California</p>
                <Link
                  to={`/board-and-care#${f.id}`}
                  data-testid={`home-preview-cta-${f.id}`}
                  onClick={() => trackEvent("explore_this_home", { facility: f.name })}
                  className="mt-4 inline-flex items-center gap-2 text-[0.92rem] font-medium text-forest transition-colors hover:text-clay"
                >
                  Explore This Home
                  <ArrowRight
                    className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function TheWay() {
  return (
    <section className="grain bg-forest py-20 text-ivory md:py-28 lg:py-32">
      <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
        <Reveal>
          <Eyebrow tone="sage">How it works</Eyebrow>
          <h2 className="mt-6 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight md:text-4xl lg:text-[2.9rem]">
            Thoughtful guidance, one step at a time.
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-[1.7] text-ivory/80">
            From the first conversation to the next chapter, CareCraft helps families explore their
            options with greater clarity and confidence.
          </p>
        </Reveal>
        <div className="relative mt-16">
          <div
            className="absolute left-0 right-0 top-7 hidden h-px bg-ivory/15 lg:block"
            aria-hidden="true"
          />
          <ol className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5 lg:gap-8">
            {HOME_STEPS.map((s, i) => (
              <Reveal key={s.num} delay={0.06 * i}>
                <li className="relative" data-testid={`home-step-${s.num}`}>
                  <p
                    className="relative z-10 font-serif text-4xl italic text-clay lg:bg-forest lg:pr-3"
                    aria-hidden="true"
                  >
                    {s.num}
                  </p>
                  <h3 className="mt-4 text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-ivory">
                    {s.title}
                  </h3>
                  <p className="mt-2.5 max-w-[26ch] text-[0.95rem] leading-[1.65] text-ivory/75">
                    {s.body}
                  </p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function DorisPreview() {
  return (
    <section className="bg-warmwhite py-20 md:py-28">
      <div className="mx-auto grid max-w-site items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
        <div className="order-2 lg:order-1 lg:col-span-5">
          <Reveal>
            <div className="mx-auto max-w-[340px]">
              <div className="overflow-hidden rounded-full border border-inkline shadow-[0_20px_50px_rgba(20,46,40,0.15)]">
                <img
                  src={dorisPortrait}
                  alt="Doris, founder of CareCraft"
                  data-testid="founder-photo-home"
                  loading="lazy"
                  className="aspect-square w-full object-cover"
                />
              </div>
            </div>
          </Reveal>
        </div>
        <div className="order-1 lg:order-2 lg:col-span-7">
          <Reveal>
            <Eyebrow>The person behind CareCraft</Eyebrow>
            <h2 className="mt-6 max-w-xl font-serif text-3xl font-medium leading-tight tracking-tight text-forest md:text-4xl lg:text-[2.9rem]">
              Care that begins with listening.
            </h2>
            <p className="mt-6 max-w-xl text-lg leading-[1.7] text-charcoal/80 md:text-xl">
              CareCraft grew from Doris's desire to serve families and her belief that thoughtful
              care begins with understanding the person. That belief continues to guide every family
              CareCraft supports.
            </p>
            <CTAButton to="/about" className="mt-8" testId="founder-cta-meet-doris">
              Meet Doris
            </CTAButton>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FamilyExperience() {
  const t = TESTIMONIALS;
  return (
    <section className="bg-ivory py-20 md:py-28">
      <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
        <Reveal>
          <Eyebrow>{t.eyebrow}</Eyebrow>
          <p className="mt-6 max-w-2xl text-lg leading-[1.7] text-charcoal/75">{t.intro}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <figure className="mx-auto max-w-3xl py-8 text-center md:py-12">
            <blockquote className="font-serif text-2xl italic leading-[1.5] text-forest md:text-[2rem]">
              "{t.featured.quote}"
            </blockquote>
            <figcaption className="mt-8">
              <span className="block font-medium text-forest">{t.featured.name}</span>
              <span className="mt-0.5 block text-[0.8rem] text-charcoal/55">{t.featured.meta}</span>
            </figcaption>
          </figure>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="text-center">
            <Link
              to="/family-experiences"
              data-testid="home-link-family-experiences"
              className="inline-flex items-center gap-2 text-[0.95rem] font-medium text-forest transition-colors hover:text-clay"
            >
              Read more family experiences
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ProfessionalsBand() {
  const highlights = ["Personally vetted options", "Local knowledge", "Response within 24 hours"];
  return (
    <section className="bg-sage-mist py-20 md:py-24">
      <div className="mx-auto grid max-w-site items-center gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>For healthcare professionals</Eyebrow>
            <h2 className="mt-6 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight text-forest md:text-4xl">
              Working together to support families well.
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-[1.7] text-charcoal/80">
              CareCraft partners with healthcare and community professionals to help families
              explore thoughtfully selected senior care options across Los Angeles.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.12}>
            <ul className="space-y-3">
              {highlights.map((h) => (
                <li key={h} className="flex items-center gap-3 font-medium text-forest">
                  <span className="h-1.5 w-1.5 rounded-full bg-clay" aria-hidden="true" />
                  {h}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <CTAButton to="/for-professionals" testId="referral-band-cta">
                Refer a Family
              </CTAButton>
              <a
                href={SITE.phoneHref}
                data-testid="referral-band-call"
                className="inline-flex min-h-[48px] items-center gap-2 font-medium text-forest transition-colors hover:text-clay"
              >
                <Phone className="h-4 w-4 text-clay" aria-hidden="true" />
                {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="grain relative overflow-hidden bg-forest py-24 text-ivory md:py-32">
      <div
        className="pointer-events-none absolute -left-32 -bottom-32 h-[420px] w-[420px] rounded-full bg-forest-dark"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-site px-4 sm:px-6 lg:px-12">
        <div className="max-w-3xl">
          <Reveal>
            <h2 className="font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl lg:text-[3.4rem]">
              Let's find the right way forward, together.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-lg leading-[1.7] text-ivory/85 md:text-xl">
              Share what matters most to you and your family. CareCraft will listen, help you
              explore the options, and guide you toward care that fits.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <CTAButton to="/contact" variant="ivory" testId="final-cta-talk-with-doris">
                Talk With Doris
              </CTAButton>
              <a
                href={SITE.phoneHref}
                data-testid="final-cta-phone"
                className="inline-flex min-h-[48px] items-center gap-2 font-medium text-ivory transition-colors hover:text-clay"
              >
                <Phone className="h-4 w-4 text-clay" aria-hidden="true" />
                {SITE.phoneDisplay}
              </a>
            </div>
            <p className="mt-12 font-serif text-2xl italic text-sage">{SITE.tagline}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  usePageMeta({
    title: null,
    description:
      "CareCraft helps Los Angeles families understand their options and find senior care that fits the person: Board & Care, Independent Living, Assisted Living, and Caregiver Support. Complimentary guidance for families.",
    path: "/",
  });

  return (
    <>
      <Hero />
      <Navigator />
      <CareOptions />
      <HomesPreview />
      <TheWay />
      <DorisPreview />
      <FamilyExperience />
      <ProfessionalsBand />
      <FinalCta />
    </>
  );
}
